import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { AlertCircle, ArrowRight, Zap, BarChart3, ShieldCheck } from "lucide-react";
import { ZestLogo } from "../components/ZestLogo";

export default function RegisterPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      await register(name, email, password);
      navigate("/setup");
    } catch (err) {
      setError(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen overflow-hidden grid lg:grid-cols-2" data-testid="register-page">
      {/* Left - Form side */}
      <div className="relative flex flex-col h-full overflow-y-auto">
        {/* Logo */}
        <div className="px-6 sm:px-10 lg:px-14 py-6">
          <Link to="/"><ZestLogo height="h-9" /></Link>
        </div>

        {/* Form - centered vertically */}
        <div className="flex-1 flex items-center px-6 sm:px-10 lg:px-14">
          <div className="w-full max-w-sm">
            <div className="mb-6">
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Create your account
              </h1>
              <p className="text-sm text-muted-foreground mt-1.5">
                Get your AI phone agent running in under 30 minutes
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 text-red-600 text-sm border border-red-100" data-testid="register-error">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  {error}
                </div>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="name" className="text-sm font-medium">Your name</Label>
                <Input id="name" placeholder="Tony Soprano" value={name} onChange={(e) => setName(e.target.value)} required data-testid="register-name-input" className="h-11 bg-white" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-sm font-medium">Email</Label>
                <Input id="email" type="email" placeholder="owner@restaurant.com" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="register-email-input" className="h-11 bg-white" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                <Input id="password" type="password" placeholder="At least 6 characters" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="register-password-input" className="h-11 bg-white" />
              </div>
              <Button
                type="submit"
                className="w-full h-11 text-sm font-semibold mt-1 shadow-md shadow-primary/20"
                disabled={loading}
                data-testid="register-submit-button"
              >
                {loading ? "Creating account..." : "Create account"}
                {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
              </Button>
            </form>

            <p className="mt-5 text-center text-sm text-muted-foreground">
              Already have an account?{" "}
              <Link to="/login" className="text-primary font-semibold hover:underline" data-testid="go-to-login-link">Sign in</Link>
            </p>
          </div>
        </div>

        {/* Trust signals - pinned at bottom */}
        <div className="hidden sm:flex items-center gap-6 px-6 sm:px-10 lg:px-14 py-5 border-t border-slate-100">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Zap className="w-3.5 h-3.5 text-amber-500" />
            <span>24/7 AI Agent</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <BarChart3 className="w-3.5 h-3.5 text-blue-500" />
            <span>Live Analytics</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="w-3.5 h-3.5 text-green-500" />
            <span>POS Integrated</span>
          </div>
        </div>
      </div>

      {/* Right - Hero Image */}
      <div className="hidden lg:block relative overflow-hidden m-3 rounded-2xl">
        <img
          src="https://images.unsplash.com/photo-1721113602610-c32a70248a79?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxwaXp6YSUyMHJlc3RhdXJhbnQlMjBpbnRlcmlvciUyMG1vZGVybiUyMGJyaWdodHxlbnwwfHx8fDE3NzI0NzI2NDB8MA&ixlib=rb-4.1.0&q=85"
          alt="Restaurant interior"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <h2 className="text-2xl xl:text-3xl font-bold mb-2 leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Never miss a phone order again
          </h2>
          <p className="text-sm xl:text-base text-white/80 max-w-md leading-relaxed">
            AI answers your restaurant calls 24/7, takes orders, and sends them straight to your POS.
          </p>
        </div>
      </div>
    </div>
  );
}
